import { useEffect, useState } from 'react';
import { parseAsString, useQueryStates } from 'nuqs';
import { toast } from 'sonner';
import type { CommissionEntryDto } from '@barbeiropro/contracts';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataColumn } from '@/components/shared/data-table';
import { useTableParams } from '@/components/shared/use-table-params';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { errorMessage } from '@/lib/api';
import { fmtDate, money } from '@/lib/format';
import { useBarbers } from '@/lib/org';
import { useSession } from '@/lib/session';
import { useCommissions, usePayCommissions } from '../api';
import { COMMISSION_STATUS_LABEL } from '../labels';
import { Pill } from '../pill';
import { PeriodFilter, usePeriodParams } from '../period';
import { ruleValue } from './rules-tab';

const ALL = 'all';
const tone = { pending: 'warn', paid: 'good', reversed: 'bad' } as const;

function Total({ label, cents }: { label: string; cents: number }) {
  return (
    <div className="grid gap-0.5 bg-card p-4">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="tabular text-xl font-semibold">{money(cents)}</dd>
    </div>
  );
}

export function StatementTab() {
  const { can } = useSession();
  const period = usePeriodParams();
  const params = useTableParams({ pageSize: 20 });
  const barbers = useBarbers();
  const [filters, setFilters] = useQueryStates({ barbeiro: parseAsString, situacao: parseAsString }, { history: 'replace' });
  const seeAll = can('commissions.read_all');
  const manage = can('commissions.manage');
  const q = useCommissions({ ...params.query, from: period.from, to: period.to, barberId: seeAll ? filters.barbeiro : null, status: filters.situacao });
  const pay = usePayCommissions();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => setSelected(new Set()), [q.data]);

  const pendingRows = (q.data?.data ?? []).filter((r) => r.status === 'pending');
  const allSelected = pendingRows.length > 0 && pendingRows.every((r) => selected.has(r.id));
  const selectedTotal = (q.data?.data ?? []).filter((r) => selected.has(r.id)).reduce((s, r) => s + r.amountCents, 0);

  const toggle = (id: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const columns: DataColumn<CommissionEntryDto>[] = [
    ...(manage
      ? [
          {
            id: 'select',
            header: () => (
              <Checkbox
                aria-label="Selecionar todas as pendentes da página"
                checked={allSelected}
                disabled={pendingRows.length === 0}
                onCheckedChange={(v) => setSelected(v === true ? new Set(pendingRows.map((r) => r.id)) : new Set())}
              />
            ),
            cell: ({ row }) =>
              row.original.status === 'pending' ? (
                <Checkbox aria-label={`Selecionar comissão de ${row.original.customerName ?? 'atendimento'}`} checked={selected.has(row.original.id)} onCheckedChange={(v) => toggle(row.original.id, v === true)} />
              ) : null,
            meta: { className: 'w-10' },
          } as DataColumn<CommissionEntryDto>,
        ]
      : []),
    { header: 'Data', accessorFn: (r) => r.createdAt, cell: ({ row }) => <span className="tabular">{fmtDate(row.original.createdAt, period.tz)}</span> },
    ...(seeAll ? [{ header: 'Profissional', accessorKey: 'barberName' } as DataColumn<CommissionEntryDto>] : []),
    { header: 'Cliente', accessorFn: (r) => r.customerName, cell: ({ row }) => row.original.customerName ?? '-' },
    { header: 'Serviço', accessorFn: (r) => r.serviceName, cell: ({ row }) => row.original.serviceName ?? '-' },
    { header: 'Base', accessorFn: (r) => r.baseCents, cell: ({ row }) => money(row.original.baseCents), meta: { align: 'right' } },
    { header: 'Regra', id: 'rule', cell: ({ row }) => <span className="tabular">{ruleValue({ type: row.original.ruleType, value: row.original.ruleValue })}</span> },
    { header: 'Comissão', accessorFn: (r) => r.amountCents, cell: ({ row }) => <span className="font-medium">{money(row.original.amountCents)}</span>, meta: { align: 'right' } },
    {
      header: 'Situação',
      id: 'status',
      cell: ({ row }) => (
        <Pill tone={tone[row.original.status]}>
          {COMMISSION_STATUS_LABEL[row.original.status]}
          {row.original.paidAt ? ` em ${fmtDate(row.original.paidAt, period.tz)}` : ''}
        </Pill>
      ),
    },
  ];

  const totals = q.data?.totals;

  return (
    <div className="grid gap-4">
      <PeriodFilter from={period.from} to={period.to} tz={period.tz} onChange={(v) => { period.set(v); void params.set({ page: 1 }); }} />
      <dl className="grid gap-px overflow-hidden rounded-lg border bg-border sm:grid-cols-3">
        <Total label="Pendente" cents={totals?.pendingCents ?? 0} />
        <Total label="Pago" cents={totals?.paidCents ?? 0} />
        <Total label="Estornado" cents={totals?.reversedCents ?? 0} />
      </dl>
      <DataTable
        columns={columns}
        data={q.data}
        params={params}
        isLoading={q.isLoading}
        error={q.error}
        onRetry={() => q.refetch()}
        searchPlaceholder="Buscar por cliente ou serviço"
        empty={{ title: 'Nenhuma comissão no período', description: 'As comissões são geradas quando um atendimento é recebido no caixa.' }}
        filters={
          <>
            {seeAll && (
              <Select value={filters.barbeiro ?? ALL} onValueChange={(v) => { void setFilters({ barbeiro: v === ALL ? null : v }); void params.set({ page: 1 }); }}>
                <SelectTrigger aria-label="Profissional" className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Todos os profissionais</SelectItem>
                  {(barbers.data ?? []).map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Select value={filters.situacao ?? ALL} onValueChange={(v) => { void setFilters({ situacao: v === ALL ? null : v }); void params.set({ page: 1 }); }}>
              <SelectTrigger aria-label="Situação" className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todas as situações</SelectItem>
                <SelectItem value="pending">Pendentes</SelectItem>
                <SelectItem value="paid">Pagas</SelectItem>
                <SelectItem value="reversed">Estornadas</SelectItem>
              </SelectContent>
            </Select>
          </>
        }
        toolbar={
          manage && (
            <ConfirmDialog
              destructive={false}
              trigger={<Button disabled={selected.size === 0 || pay.isPending}>Marcar como pagas{selected.size ? ` (${selected.size})` : ''}</Button>}
              title="Marcar comissões como pagas?"
              description={`${selected.size} ${selected.size === 1 ? 'comissão' : 'comissões'}, totalizando ${money(selectedTotal)}. Isso registra que o valor já foi repassado ao profissional.`}
              confirmLabel="Marcar como pagas"
              onConfirm={async () => {
                try {
                  const r = await pay.mutateAsync([...selected]);
                  toast.success(`${r.updated} ${r.updated === 1 ? 'comissão marcada' : 'comissões marcadas'} como paga${r.updated === 1 ? '' : 's'}.`);
                  setSelected(new Set());
                } catch (e) {
                  toast.error(errorMessage(e));
                }
              }}
            />
          )
        }
      />
    </div>
  );
}
